/**
 * Merge zweier Tickets (Spec 7.2) mit dem Duplikat-Hinweis aus Spec 9.3.
 *
 * Zwei Wege zum selben Ziel:
 *   - **Vorschlag:** war die Aehnlichkeit sehr hoch, steht der Kandidat oben
 *     mit einem „Zusammenführen"-Knopf daneben. Der Hinweis ist ein Angebot,
 *     nie eine Automatik.
 *   - **Von Hand:** Ticketnummer eintippen.
 *
 * Die Rueckfrage „Sind das dieselbe Person?" (Spec 7.2) erscheint nur, wenn
 * die beiden Tickets **unterschiedliche** Kontakte haben. Ohne ausdrueckliches
 * Ja bleiben sie getrennt.
 */
import type { SimilarTicket, TicketDetail } from '@tickethub/shared';
import { Merge } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type MergeRequest = { targetTicketId: string; mergeContacts: boolean };

type MergePanelProps = {
  ticket: TicketDetail;
  /** Kandidaten aus der Aehnlichkeitssuche (Spec 9.5) — inklusive Duplikat-Flag. */
  similar: SimilarTicket[];
  disabled: boolean;
  onMerge: (request: MergeRequest) => Promise<void>;
};

export function MergePanel({ ticket, similar, disabled, onMerge }: MergePanelProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [targetId, setTargetId] = useState('');
  const [mergeContacts, setMergeContacts] = useState(false);
  const [busy, setBusy] = useState(false);

  const duplicates = similar.filter((entry) => entry.duplicate_candidate);
  const alreadyMerged = ticket.merged_into_ticket_id !== null;

  async function submit(target: string) {
    if (target.trim().length === 0) return;
    setBusy(true);
    try {
      await onMerge({ targetTicketId: target.trim(), mergeContacts });
      setOpen(false);
      setTargetId('');
      setMergeContacts(false);
    } finally {
      setBusy(false);
    }
  }

  if (alreadyMerged) {
    return (
      <Alert>
        <AlertDescription>{t('merge.already_merged')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border p-4" aria-label={t('merge.title')}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium">{t('merge.title')}</h2>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={() => setOpen((value) => !value)}
        >
          <Merge aria-hidden="true" />
          {open ? t('common.cancel') : t('merge.start')}
        </Button>
      </div>

      {/* Duplikat-Hinweis (Spec 9.3): nur bei sehr hoher Aehnlichkeit. */}
      {duplicates.length > 0 ? (
        <Alert>
          <AlertDescription className="space-y-2">
            <p>{t('merge.duplicate_hint')}</p>
            <ul className="space-y-1">
              {duplicates.map((entry) => (
                <li key={entry.ticket_id} className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-xs">{entry.number}</span>
                  <span className="truncate">{entry.subject}</span>
                  <Badge variant="outline">{entry.score.toFixed(2)}</Badge>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="ml-auto"
                    disabled={disabled || busy}
                    onClick={() => void submit(entry.ticket_id)}
                  >
                    {t('merge.merge_into')}
                  </Button>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {open ? (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            void submit(targetId);
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="merge-target">{t('merge.target_label')}</Label>
            <Input
              id="merge-target"
              value={targetId}
              placeholder={t('merge.target_placeholder')}
              onChange={(event) => setTargetId(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t('merge.target_help')}</p>
          </div>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={mergeContacts}
              onChange={(event) => setMergeContacts(event.target.checked)}
            />
            <span>
              {t('merge.same_person')}
              <span className="block text-xs text-muted-foreground">
                {t('merge.same_person_help')}
              </span>
            </span>
          </label>

          <Button type="submit" size="sm" disabled={disabled || busy || targetId.trim() === ''}>
            {busy ? t('common.saving') : t('merge.confirm')}
          </Button>
        </form>
      ) : null}
    </section>
  );
}
